import UtmsButton from '@shared/components/ui/UtmsButton';
import TuneIcon from '@mui/icons-material/Tune';
import ModalSimple from '@shared/components/modals/ModalSimple';
import Flow from '@pages/schemes/components/Flow/Flow';
import { utmsButtonSx } from '@shared/constants/otherConstants';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Scheme } from '@pages/schemes/types';
import { Dispatch, FC, FormEvent, memo, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { selectDeployedSchemesForEntity } from '@pages/stands/store/selectors';

interface SchemeConfigurationProps {
    scheme: Scheme;
    setSchemesToDeploy: Dispatch<SetStateAction<Scheme[]>>;
}

const SchemeConfiguration: FC<SchemeConfigurationProps> = memo(({ scheme, setSchemesToDeploy }) => {
    const { t } = useTranslation();
    const [configMode, setConfigMode] = useState<'default' | 'custom'>('default');
    const [openParamsConfigModal, setOpenParamsConfigModal] = useState(false);
    const [localNodes, setLocalNodes] = useState(structuredClone(scheme.json_config.nodes));
    const [localEdges, setLocalEdges] = useState(structuredClone(scheme.json_config.edges));
    const deployedSchemesForEntity = useSelector(selectDeployedSchemesForEntity);

    const handleOpenParamsConfigModal = useCallback(() => {
        setOpenParamsConfigModal(true);
    }, []);

    const handleCloseParamsConfigModal = useCallback(() => {
        setOpenParamsConfigModal(false);
    }, []);

    const handleApplyNewNodesParams = useCallback(
        (newJsonConfig: Scheme['json_config']) => (_e: FormEvent<HTMLFormElement>) => {
            setSchemesToDeploy((allSchemes) => {
                return allSchemes.map((schemeToDeploy) => {
                    if (schemeToDeploy.pk === scheme.pk) {
                        return {
                            ...scheme,
                            json_config: newJsonConfig,
                        };
                    }

                    return schemeToDeploy;
                });
            });

            handleCloseParamsConfigModal();
        },
        [handleCloseParamsConfigModal, scheme, setSchemesToDeploy],
    );

    const handleResetToDefaultParams = useCallback(() => {
        const currentSchemeBeforeModify = deployedSchemesForEntity.find(
            (readonlyScheme: Scheme) => readonlyScheme.pk === scheme.pk,
        );

        if (!currentSchemeBeforeModify) {
            return;
        }

        const defaultNodes = currentSchemeBeforeModify.json_config.nodes;
        const defaultEdges = currentSchemeBeforeModify.json_config.edges;

        setLocalNodes(structuredClone(defaultNodes));
        setLocalEdges(structuredClone(defaultEdges));
        handleApplyNewNodesParams({ nodes: defaultNodes, edges: defaultEdges });
    }, [deployedSchemesForEntity, handleApplyNewNodesParams, scheme.pk]);

    const handleSelectChange = useCallback(
        (e: SelectChangeEvent) => {
            const value = e.target.value as 'default' | 'custom';
            setConfigMode(value);
            value === 'default' && handleResetToDefaultParams();
        },
        [handleResetToDefaultParams],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <FormControl fullWidth size="small">
                <Select value={configMode} onChange={handleSelectChange}>
                    <MenuItem value="default">{t('default')}</MenuItem>
                    <MenuItem value="custom">{t('custom')}</MenuItem>
                </Select>
            </FormControl>
            {configMode === 'custom' && (
                <UtmsButton
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<TuneIcon />}
                    variant="outlined"
                    onClick={handleOpenParamsConfigModal}
                >
                    {t('configure')}
                </UtmsButton>
            )}
            {/*@ts-ignore */}
            <ModalSimple
                open={openParamsConfigModal}
                handleClose={handleCloseParamsConfigModal}
                content={
                    <Box sx={{ width: '100%', height: 600 }}>
                        {/*@ts-ignore */}
                        <Flow nodes={localNodes} edges={localEdges} configureOnly />
                    </Box>
                }
                action={t('apply')}
                submitButtonIcon={<SaveIcon />}
                customActions={
                    <UtmsButton
                        variant="contained"
                        endIcon={<RestartAltIcon />}
                        onClick={handleResetToDefaultParams}
                        sx={utmsButtonSx}
                    >
                        {t('reset_to_default')}
                    </UtmsButton>
                }
                title={scheme.name}
                handleSubmit={handleApplyNewNodesParams({ nodes: localNodes, edges: localEdges })}
                DialogProps={{
                    fullWidth: true,
                    maxWidth: 'xl',
                }}
            />
        </Box>
    );
});

export default SchemeConfiguration;
